import { getAllStablePoolsFromCache, getPoolsByTokens, Pool } from './pool';
import {
  ftGetTokenMetadata,
  ftGetTokensMetadata,
  TokenMetadata,
} from './ft-contract';
import {
  getSinglePoolEstimate,
  getStablePoolThisPair,
  getStablePoolEstimate,
  getPoolEstimate,
  getStablePoolInfoThisPair,
} from './swapPoolEstimates';
import { PoolMode } from './swapTypes';
import { isStablePool, isStableToken } from './near';
import _ from 'lodash';
import { toNonDivisibleNumber, toReadableNumber } from '../utils/numbers';
import Big from 'big.js';

export const getOneSwapActionResultAurora = async (
  poolsOneSwap: Pool[],
  tokenIn: TokenMetadata,
  tokenOut: TokenMetadata,
  throwNoPoolError: (error?: any) => void,
  parsedAmountIn: string
) => {
  let triTodos;
  const pools: Pool[] = poolsOneSwap;

  const triPoolThisPair = pools.find(
    (pool) =>
      pool.Dex === 'tri' &&
      pool.tokenIds &&
      pool.tokenIds.includes(tokenIn.id) &&
      pool.tokenIds.includes(tokenOut.id)
  );

  if (!triPoolThisPair || !pools || pools.length === 0) {
    throwNoPoolError();
  }

  if (triPoolThisPair) {
    const triPoolEstimateRes = getSinglePoolEstimate(
      tokenIn,
      tokenOut,
      triPoolThisPair,
      parsedAmountIn
    );

    triTodos = [
      {
        ...triPoolEstimateRes,
        status: PoolMode.PARALLEL,
        routeInputToken: tokenIn.id,
        totalInputAmount: parsedAmountIn,
        pool: {
          ...triPoolThisPair,
          partialAmountIn: parsedAmountIn,
        },
        tokens: [tokenIn, tokenOut],
        inputToken: tokenIn.id,
        outputToken: tokenOut.id,
      },
    ];
  }

  return {
    triTodos,
  };
};

export const getOneSwapActionResult = async (
  poolsOneSwap: Pool[],
  loadingTrigger: boolean,
  tokenIn: TokenMetadata,
  tokenOut: TokenMetadata,
  supportLedger: boolean,
  throwNoPoolError: (error?: any) => void,
  amountIn: string,
  parsedAmountIn: string
) => {
  const { allStablePoolsById, allStablePools } =
    await getAllStablePoolsFromCache(loadingTrigger);

  let supportLedgerRes;

  /**
   * for swap pro, we need to calculate the result on tri pool
   * to do price comparison on tri result and ref result
   *
   */

  let pools: Pool[] = poolsOneSwap;

  if (isStableToken(tokenIn.id) && isStableToken(tokenOut.id)) {
    pools = pools.concat(
      getStablePoolThisPair({
        tokenInId: tokenIn.id,
        tokenOutId: tokenOut.id,
        stablePools: allStablePools,
      })
    );
  }

  /**s
   *  single swap action estimate for support ledger and swap pro mode
   *
   */
  if (pools.length === 0 && supportLedger) {
    throwNoPoolError();
  }

  if (pools.length > 0) {
    const bestPricePool =
      pools.length === 1
        ? pools[0]
        : _.maxBy(pools, (pool) => {
            if (isStablePool(pool.id)) {
              return Number(
                getStablePoolEstimate({
                  tokenIn,
                  tokenOut,
                  stablePool: allStablePoolsById[pool.id][0],
                  stablePoolInfo: allStablePoolsById[pool.id][1],
                  amountIn,
                }).estimate
              );
            }
            return Number(
              getSinglePoolEstimate(tokenIn, tokenOut, pool, parsedAmountIn)
                .estimate
            );
          });

    const estimateRes = await getPoolEstimate({
      tokenIn,
      tokenOut,
      amountIn: parsedAmountIn,
      Pool: bestPricePool,
    });

    const parallelEstimate = [
      {
        ...estimateRes,
        status: PoolMode.PARALLEL,
        routeInputToken: tokenIn.id,
        totalInputAmount: parsedAmountIn,
        pool: {
          ...bestPricePool,
          partialAmountIn: parsedAmountIn,
        },
        tokens: [tokenIn, tokenOut],
        inputToken: tokenIn.id,
        outputToken: tokenOut.id,
        parsedAmountIn,
      },
    ];

    supportLedgerRes = parallelEstimate;
  }

  // get result on tri pools but just one swap action

  return {
    supportLedgerRes,
  };
};

// hybrid stable pool
export async function getHybridStableSmart(
  tokenIn: TokenMetadata,
  tokenOut: TokenMetadata,
  amountIn: string,
  loadingTrigger: boolean
) {
  const parsedAmountIn = toNonDivisibleNumber(tokenIn.decimals, amountIn);

  let firstHopPool: Pool, secondHopPool: Pool;

  let firstHopPools: Pool[] = [];
  const secondHopPools: Pool[] = [];

  const pools1Right: Pool[] = [];
  let pools2Right: Pool[] = [];

  const { allStablePools, allStablePoolsById, allStablePoolsInfo } =
    await getAllStablePoolsFromCache(loadingTrigger);

  const candidatePools: Pool[][] = [];

  /**
   * find possible routes for this pair
   *
   *
   */
  if (isStableToken(tokenIn.id)) {
    // first hop will be through stable pool.
    firstHopPools = allStablePools.filter((pool) =>
      pool.tokenIds.includes(tokenIn.id)
    );

    const otherStables = firstHopPools
      .map((pool) => pool.tokenIds.filter((id) => id !== tokenIn.id))
      .flat();

    for (const otherStable of otherStables) {
      const stablePools = getStablePoolThisPair({
        tokenInId: otherStable,
        tokenOutId: tokenOut.id,
        stablePools: allStablePools,
      });

      const { filteredPools: tmpPools } = await getPoolsByTokens({
        tokenInId: otherStable,
        tokenOutId: tokenOut.id,
        amountIn: parsedAmountIn,
        loadingTrigger: false,
      });
      const tobeAddedPools = tmpPools.concat(stablePools);
      secondHopPools.push(
        ...tobeAddedPools.filter((pool: any) => {
          const supplies = Object.values(pool.supplies) as any;
          return new Big(supplies[0]).times(new Big(supplies[1])).gt(0);
        })
      );
    }
  }

  if (isStableToken(tokenOut.id)) {
    // second hop will be through stable pool.
    pools2Right = allStablePools.filter((pool) =>
      pool.tokenIds.includes(tokenOut.id)
    );

    const otherStables = pools2Right
      .map((pool) => pool.tokenIds.filter((id) => id !== tokenOut.id))
      .flat();
    for (const otherStable of otherStables) {
      const stablePools = getStablePoolThisPair({
        tokenInId: tokenIn.id,
        tokenOutId: otherStable,
        stablePools: allStablePools,
      });

      const { filteredPools: tmpPools } = await getPoolsByTokens({
        tokenInId: tokenIn.id,
        tokenOutId: otherStable,
        amountIn: parsedAmountIn,
        loadingTrigger: false,
      });

      const tobeAddedPools = tmpPools.concat(stablePools);

      pools1Right.push(
        ...tobeAddedPools.filter((pool) => {
          const supplies = Object.values(pool.supplies);
          return new Big(supplies[0]).times(new Big(supplies[1])).gt(0);
        })
      );
    }
  }

  // find candidate pools

  for (const firstHopPool of firstHopPools) {
    const middleTokens = firstHopPool.tokenIds.filter(
      (id: string) => id !== tokenIn.id
    );
    for (const middleToken of middleTokens) {
      const p2s = secondHopPools.filter(
        (pool) =>
          pool.tokenIds.includes(middleToken) &&
          pool.tokenIds.includes(tokenOut.id) &&
          middleToken !== tokenOut.id
      );
      let secondHopPool = _.maxBy(p2s, (pool) =>
        Number(
          new Big(
            toReadableNumber(tokenOut.decimals, pool.supplies[tokenOut.id])
          )
        )
      );

      if (middleToken === tokenOut.id) {
        secondHopPool = firstHopPool;
      }

      if (firstHopPool && secondHopPool) {
        if (firstHopPool.id === secondHopPool.id)
          candidatePools.push([firstHopPool]);
        else candidatePools.push([firstHopPool, secondHopPool]);
      }
    }
  }
  for (const firstHopPool of pools1Right) {
    const middleTokens = firstHopPool.tokenIds.filter(
      (id: string) => id !== tokenIn.id
    );
    for (const middleToken of middleTokens) {
      const p2s = pools2Right.filter(
        (pool) =>
          pool.tokenIds.includes(middleToken) &&
          pool.tokenIds.includes(tokenOut.id) &&
          middleToken !== tokenOut.id
      );
      let secondHopPool = _.maxBy(p2s, (pool) =>
        Number(
          new Big(
            toReadableNumber(tokenOut.decimals, pool.supplies[tokenOut.id])
          )
        )
      );

      if (middleToken === tokenOut.id) {
        secondHopPool = firstHopPool;
      }

      if (firstHopPool && secondHopPool) {
        if (firstHopPool.id === secondHopPool.id)
          candidatePools.push([firstHopPool]);
        else candidatePools.push([firstHopPool, secondHopPool]);
      }
    }
  }

  if (candidatePools.length > 0) {
    const tokensMetadata = await ftGetTokensMetadata(
      candidatePools
        .map((poolPair) => poolPair.map((pool) => pool.tokenIds).flat())
        .flat()
    );

    const BestPoolPair =
      candidatePools.length === 1
        ? candidatePools[0]
        : _.maxBy(candidatePools, (poolPair) => {
            // only one pool case, only for stable tokens
            if (poolPair.length === 1) {
              if (isStablePool(poolPair[0].id)) {
                return Number(
                  getStablePoolEstimate({
                    tokenIn,
                    tokenOut,
                    stablePool: getStablePoolThisPair({
                      tokenInId: tokenIn.id,
                      tokenOutId: tokenOut.id,
                      stablePools: allStablePools,
                    })[0],
                    amountIn,
                    stablePoolInfo: getStablePoolInfoThisPair({
                      tokenInId: tokenIn.id,
                      tokenOutId: tokenOut.id,
                      stablePoolsInfo: allStablePoolsInfo,
                    })[0],
                  }).estimate
                );
              } else {
                return Number(
                  getSinglePoolEstimate(
                    tokenIn,
                    tokenOut,
                    poolPair[0],
                    parsedAmountIn
                  ).estimate
                );
              }
            }

            const [firstPool, secondPool] = poolPair;
            const tokenMidId = poolPair[0].tokenIds.find(
              (tokenId: string) =>
                poolPair[1].tokenIds.includes(tokenId) &&
                tokenId !== tokenIn.id &&
                tokenId != tokenOut.id
            );

            const tokenMidMeta = tokensMetadata[tokenMidId];

            const firstHopEstimate = {
              ...(isStablePool(firstPool.id)
                ? getStablePoolEstimate({
                    tokenIn,
                    tokenOut: tokenMidMeta,
                    amountIn,
                    stablePoolInfo: allStablePoolsById[firstPool.id][1],
                    stablePool: allStablePoolsById[firstPool.id][0],
                  })
                : getSinglePoolEstimate(
                    tokenIn,
                    tokenMidMeta,
                    firstPool,
                    parsedAmountIn
                  )),
              status: PoolMode.SMART,
            };

            const secondHopEstimate = {
              ...(isStablePool(secondPool.id)
                ? getStablePoolEstimate({
                    tokenIn: tokenMidMeta,
                    tokenOut,
                    amountIn: firstHopEstimate.estimate,
                    stablePoolInfo: allStablePoolsById[secondPool.id][1],
                    stablePool: allStablePoolsById[secondPool.id][0],
                  })
                : getSinglePoolEstimate(
                    tokenMidMeta,
                    tokenOut,
                    secondPool,
                    toNonDivisibleNumber(
                      tokenMidMeta.decimals,
                      firstHopEstimate.estimate
                    )
                  )),
              status: PoolMode.SMART,
            };

            return Number(secondHopEstimate.estimate);
          });

    // one pool case only get best price

    if (!BestPoolPair) return { actions: [], estimate: '0' };

    if (BestPoolPair.length === 1) {
      const bestPool = BestPoolPair[0];
      const estimate = await getPoolEstimate({
        tokenIn,
        tokenOut,
        amountIn: parsedAmountIn,
        Pool: bestPool,
      });

      estimate.pool.partialAmountIn = parsedAmountIn;

      return {
        actions: [
          {
            ...estimate,
            status: PoolMode.STABLE,
            tokens: [tokenIn, tokenOut],
            inputToken: tokenIn.id,
            outputToken: tokenOut.id,
            totalInputAmount: toNonDivisibleNumber(tokenIn.decimals, amountIn),
          },
        ],
        estimate: estimate.estimate,
      };
    }

    // two pool case get best price
    [firstHopPool, secondHopPool] = BestPoolPair;

    const tokenMidId = BestPoolPair[0].tokenIds.find(
      (tokenId: string) =>
        BestPoolPair[1].tokenIds.includes(tokenId) &&
        tokenId !== tokenIn.id &&
        tokenId != tokenOut.id
    );

    const tokenMidMeta = await ftGetTokenMetadata(tokenMidId);

    const firstHopEstimate = {
      ...(isStablePool(firstHopPool.id)
        ? getStablePoolEstimate({
            tokenIn,
            tokenOut: tokenMidMeta,
            amountIn,
            stablePoolInfo: allStablePoolsById[firstHopPool.id][1],
            stablePool: allStablePoolsById[firstHopPool.id][0],
          })
        : getSinglePoolEstimate(
            tokenIn,
            tokenMidMeta,
            firstHopPool,
            parsedAmountIn
          )),
      status: PoolMode.SMART,
      tokens: [tokenIn, tokenMidMeta, tokenOut],
      inputToken: tokenIn.id,
      outputToken: tokenMidMeta.id,
      totalInputAmount: toNonDivisibleNumber(tokenIn.decimals, amountIn),
    };

    firstHopEstimate.pool.partialAmountIn = parsedAmountIn;

    const secondHopEstimate = {
      ...(isStablePool(secondHopPool.id)
        ? getStablePoolEstimate({
            tokenIn: tokenMidMeta,
            tokenOut,
            amountIn: firstHopEstimate.estimate,
            stablePoolInfo: allStablePoolsById[secondHopPool.id][1],
            stablePool: allStablePoolsById[secondHopPool.id][0],
          })
        : getSinglePoolEstimate(
            tokenMidMeta,
            tokenOut,
            secondHopPool,
            toNonDivisibleNumber(
              tokenMidMeta.decimals,
              firstHopEstimate.estimate
            )
          )),

      status: PoolMode.SMART,
      tokens: [tokenIn, tokenMidMeta, tokenOut],
      inputToken: tokenMidMeta.id,
      outputToken: tokenOut.id,
      totalInputAmount: toNonDivisibleNumber(tokenIn.decimals, amountIn),
    };

    return {
      actions: [firstHopEstimate, secondHopEstimate],
      estimate: secondHopEstimate.estimate,
    };
  }

  return { actions: [], estimate: '0' };
}
